import { useEffect, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useDispatch } from 'react-redux';

import { PaymentStatus, PricingPlanFeature, PricingPlanSchema } from 'types';
import { getChannelURL } from 'utils';
import { profileActions } from 'store/actions';
import { profileChannelNameSelector } from 'store/selectors';
import { api, LoggerService } from 'services';
import { useRouter, useStore } from 'hooks';
import { Loader, PricingStatusCard } from 'components';

import PaymentSuccess from './Payment.Success';

export type PaymentProcessProps = {
  pricingPlan: PricingPlanSchema | null;
};

const PaymentProcess = ({ pricingPlan }: PaymentProcessProps) => {
  const stripe = useStripe();
  const dispatch = useDispatch();
  const profileChannelName = useStore(profileChannelNameSelector);

  const router = useRouter<{
    pricingPlanId: string;
    channelName?: string;
    payment_intent_client_secret: string;
  }>();
  const { payment_intent_client_secret: paymentIntentClientSecret } = router.params;

  const [status, setStatus] = useState<PaymentStatus | null>(PaymentStatus.IDLE);

  const isMembershipPricingPlanFetched = useRef(false);
  const hasMembersOnlyFeature = pricingPlan?.features.includes(PricingPlanFeature.MEMBERS_ONLY_EVENT);

  const getPaymentStatus = async () => {
    const response = await stripe?.retrievePaymentIntent(paymentIntentClientSecret);

    setStatus((response?.paymentIntent?.status as PaymentStatus) || null);
  };

  const fetchUser = async () => {
    const response = await api.requests.ltv.getUser();

    dispatch(profileActions.setUser(response.data));
  };

  useEffect(() => {
    if (!stripe || !paymentIntentClientSecret) {
      return;
    }

    setTimeout(() => getPaymentStatus(), 5_000);
  }, [stripe, paymentIntentClientSecret]);

  useEffect(() => {
    if (status === PaymentStatus.SUCCEEDED) {
      if (hasMembersOnlyFeature) {
        fetchMembershipPricingPlan();
      }

      fetchUser();
    }
  }, [status, hasMembersOnlyFeature]);

  const fetchMembershipPricingPlan = async () => {
    try {
      const {
        data: { membershipPricingPlan }
      } = await api.requests.ltv.getChannelByName(profileChannelName || '');

      dispatch(profileActions.setMembershipPricingPlan(membershipPricingPlan));

      isMembershipPricingPlanFetched.current = true;

      // If members only price does not exist then redirect user to price setting page with skipping option
      if (!membershipPricingPlan) {
        router.push('/get-paid', { skippable: true });
      }
    } catch (error) {
      LoggerService.log('FETCH_MEMBERSHIP_PRICING_PLAN', error);
    }
  };

  const handleOnProceed = () => {
    router.replace(router.params.channelName ? getChannelURL(router.params.channelName) : '/');
  };

  const renderContent = () => {
    switch (status) {
      case PaymentStatus.IDLE: {
        return <Loader global />;
      }
      case PaymentStatus.CANCELED: {
        return <PricingStatusCard type="error" title="Payment canceled. Please try another payment method." />;
      }
      case PaymentStatus.PROCESSING: {
        return <PricingStatusCard type="warn" title="Payment processing." />;
      }
      case PaymentStatus.REQUIRES_PAYMENT_METHOD: {
        return <PricingStatusCard type="error" title="Payment failed. Please try another payment method." />;
      }
      case PaymentStatus.SUCCEEDED: {
        // If the purchased plan has members only feature then wait when membership pricing plan is fetched and checked whether members only price is set
        if (hasMembersOnlyFeature && !isMembershipPricingPlanFetched.current) {
          return <Loader global />;
        }

        return <PaymentSuccess pricingPlan={pricingPlan} onProceed={handleOnProceed} />;
      }
      default: {
        return <PricingStatusCard type="error" title="Something went wrong" />;
      }
    }
  };

  return <>{renderContent()}</>;
};

export default PaymentProcess;
